import { useIntl } from 'react-intl'
import { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'LuxKit/Button/TextButton'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import {
  buildSuggestedDatesString,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'

const Root = styled.div`
  display: flex;
  flex-direction: column;

  &.align-left {
    align-items: flex-start;
  }

  &.align-center {
    align-items: center;
  }

  &.align-right {
    align-items: flex-end;
  }
`

interface Props {
  offer: App.BundleOffer
  className?: string
  rooms?: Array<App.Occupants>
  pkg: App.BundlePackageWithPrice
  align?: 'left' | 'center' | 'right' | 'none'
  offerUrl: string
  offerAvailable?: boolean
  filters?: App.OfferListFilters
  hidePrice?: boolean
}

function BundleOfferTilePricing(props: Props) {
  const {
    offer,
    className,
    pkg,
    align = 'none',
    rooms = EmptyArray,
    offerUrl,
    offerAvailable = true,
    filters,
    hidePrice = false,
  } = props

  const intl = useIntl()
  const offerMetadata = useOfferMetaData(offer.id, filters)
  const showIncludedGuestsExceeded = useMemo(() => {
    if (pkg.packages.some((p) => !p.roomType || (p.roomRate?.extraGuestSurcharges?.length || 0) > 0)) {
      return false
    }

    return rooms.some(room => pkg.packages.every(p => hasIncludedGuestsExceeded(room, p, offer.bundledOffers[p.offerId])))
  }, [pkg, rooms, offer])

  const { shouldShowDiscountPercent, hotelDiscountPercent, showPriceDetails } = useMemo(() => getBundleValueUp(offer, pkg), [offer, pkg])

  const {
    defaultPricing,
    suggestedDates,
  } = useSearchPrices({
    offer,
    filters,
    pkg,
    useRealSearchPrices: getShouldUseSearchPrices(filters),
  })

  const { price, saleUnit } = defaultPricing

  const propertyFees = defaultPricing.fees.find(fee => fee.type === 'property')?.amount ?? 0

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const duration = useMemo(() => {
    return getShouldUseSearchPrices(filters) ? offerMetadata?.pricing?.duration : pkg.duration
  }, [pkg, offerMetadata, filters])

  useEffect(() => {
    const roomRateId = getShouldUseSearchPrices(filters) ? offerMetadata?.pricing?.lowestPriceRoomRateId : undefined

    dispatchOfferListEvent({
      type: OfferListEvents.pricingCalculated,
      availability: offerAvailable,
      price: hidePrice ? undefined : defaultPricing.price,
      roomRateId: hidePrice ? undefined : roomRateId,
      memberPrice: undefined, // bundle offers do not have member pricing
      duration: hidePrice ? undefined : duration,
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerMetadata])

  const durationText = useMemo(() => {
    const isFlexibleSearch = filters?.flexibleNights && suggestedDates?.checkIn && suggestedDates?.checkOut
    const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !isFlexibleSearch
    const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut

    let text = 'from'
    if (!isAnytimeSearch && hasSuggestedCheckInCheckOut && suggestedDates) {
      text = `${buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)} from`
    } else if (duration) {
      text = intl.formatMessage(
        {
          id: 'm1DavnCJj7DflSRRi9CV',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Duration in nights followed by "from" for bundle offer tile pricing',
        },
        { count: duration },
      )
    }

    return text
  }, [duration, filters?.checkIn, filters?.checkOut, filters?.flexibleNights, suggestedDates, intl])

  if (hidePrice) {
    return null
  }

  return (
    <Root className={clsx(className, `align-${align}`)}>
      {shouldShowDiscountPercent && !!hotelDiscountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={hotelDiscountPercent}
      />}
      <PriceRowPriceCaption>{durationText}</PriceRowPriceCaption>
      <PriceRowPrice
        data-testid="search-price"
        size="L"
        price={price}
        saleUnit={saleUnit}
      />
      <PriceRowTaxesAndFees />
      {showIncludedGuestsExceeded && (
        <AdditionalGuestsPopup
          complex={false}
          description={intl.formatMessage({
            id: 'XWdOvvSoHx525pUfcTDg',
            defaultMessage: '+ Extra guest surcharge',
            description: 'Caption indicating additional surcharge for extra guests in bundle offer',
          })}
        />
      )}
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
        offerLocations={offerLocations}
        vendorName={vendorName}
      />
      {showPriceDetails && duration && <OfferPriceDetailsRow
        trigger="price-row"
        triggerSize="M"
        offer={offer}
        duration={duration}
        propertyFees={propertyFees}
        hotelPrice={price}
        rooms={rooms}
        dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
        cta={
          <TextButton kind="primary" fit="flex" to={offerUrl}>
            {intl.formatMessage({
              id: 'FEvpfAAU-Jmx7pbrlofo',
              defaultMessage: 'View Offer',
              description: 'CTA button in bundle offer price details to view the full offer',
            })}
          </TextButton>
        }
      />}
    </Root>
  )
}

export default BundleOfferTilePricing
